import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import RoomMediaManager from '../components/RoomMediaManager';
import ThemeSettings from '../components/ThemeSettings';
import PropertyOwnersManager from '../components/PropertyOwnersManager';
import EmailSettingsManager from '../components/EmailSettingsManager';
import PromoCodesManager from '../components/PromoCodesManager';
import InfoCardsManager from '../components/InfoCardsManager';
import RatesManager from '../components/RatesManager';
import RijekaButton from '../components/RijekaButton';
import { PmsSettingsCard } from '../components/PmsSettingsCard';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('settings'); // 'settings', 'media', 'theme'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [settings, setSettings] = useState({
    pms_api_key: '',
    pms_tenant_slug: '',
    pms_api_base_url: '',
    stripe_api_key: '',
    stripe_publishable_key: '',
    property_configs: []
  });
  
  const [newProperty, setNewProperty] = useState({
    property_id: '',
    property_name: '',
    room_ids: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    fetchSettings(token);
  }, [navigate]);

  const fetchSettings = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      } else {
        toast.error('Failed to load settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    setSaving(true);

    try {
      await axios.post(`${API}/admin/settings`, {
        stripe_api_key: settings.stripe_api_key,
        stripe_publishable_key: settings.stripe_publishable_key
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddProperty = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    if (!newProperty.property_id || !newProperty.property_name || !newProperty.room_ids) {
      toast.error('Please fill in all property fields');
      return;
    }

    try {
      const roomIdsArray = newProperty.room_ids.split(',').map(id => id.trim()).filter(id => id);
      
      await axios.post(`${API}/admin/properties`, {
        property_id: newProperty.property_id,
        property_name: newProperty.property_name,
        room_ids: roomIdsArray
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      toast.success('Property added successfully');
      setNewProperty({ property_id: '', property_name: '', room_ids: '' });
      fetchSettings(token);
    } catch (error) {
      console.error('Error adding property:', error);
      toast.error('Failed to add property');
    }
  };

  const handleEditProperty = (property) => {
    setEditingProperty({
      ...property,
      room_ids: property.room_ids.join(', '),
      group_name: property.group_name || '',
      display_order: property.display_order || 0,
      address: property.address || '',
      latitude: property.latitude || null,
      longitude: property.longitude || null,
      pricing_policy_hr: property.pricing_policy_hr || '',
      pricing_policy_en: property.pricing_policy_en || '',
      pricing_policy_de: property.pricing_policy_de || '',
      pricing_policy_it: property.pricing_policy_it || ''
    });
  };

  const handleSaveEdit = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    if (!editingProperty.property_id || !editingProperty.property_name || !editingProperty.room_ids) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const roomIdsArray = editingProperty.room_ids.split(',').map(id => id.trim()).filter(id => id);
      
      await axios.post(`${API}/admin/properties`, {
        property_id: editingProperty.property_id,
        property_name: editingProperty.property_name,
        room_ids: roomIdsArray,
        group_name: editingProperty.group_name || null,
        display_order: parseInt(editingProperty.display_order) || 0,
        address: editingProperty.address || null,
        latitude: editingProperty.latitude || null,
        longitude: editingProperty.longitude || null,
        pricing_policy_hr: editingProperty.pricing_policy_hr || null,
        pricing_policy_en: editingProperty.pricing_policy_en || null,
        pricing_policy_de: editingProperty.pricing_policy_de || null,
        pricing_policy_it: editingProperty.pricing_policy_it || null
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      toast.success('Property updated successfully');
      setEditingProperty(null);
      fetchSettings(token);
    } catch (error) {
      console.error('Error updating property:', error);
      toast.error('Failed to update property');
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error('Not authenticated');
      navigate('/admin');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete this property (ID: ${propertyId})?`)) {
      return;
    }

    try {
      console.log('Deleting property:', propertyId);
      const response = await axios.delete(`${API}/admin/properties/${propertyId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('Delete response:', response.data);
      toast.success('Property deleted successfully');
      
      // Refresh the settings
      await fetchSettings(token);
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to delete property');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center">
        <div className="inline-block animate-spin h-16 w-16 border-4 border-[var(--color-primary)] border-r-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Navigation - Logo Only */}
      <nav className="bg-[var(--color-dark)] text-white shadow-lg" style={{padding: '10px 0px' , borderBottom: '1px solid var(--color-primary-dark)'}}>
        <div className="container mx-auto px-6 py-5 flex justify-between items-center">
          <div 
            className="cursor-pointer hover:opacity-80 transition-opacity flex items-center" 
            onClick={() => navigate('/')}
          >
            {theme.logo_url ? (
              <img src={theme.logo_url} alt="Logo" className="h-10 w-auto" />
            ) : (
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            )}
          </div>
          <RijekaButton 
            variant="outline" 
            onClick={handleLogout}
            data-testid="logout-button"
            className="border-white text-white hover:bg-white hover:text-[var(--color-text-primary)]"
          >
            Logout
          </RijekaButton>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12" style={{padding: '50px 20px'}}>
        <div className="max-w-7xl mx-auto">
          <div className="bg-white p-8 mb-8 shadow-lg border-l-4 border-[var(--color-primary)]">
            <h2 className="text-4xl md:text-5xl mb-3 font-bold text-[var(--color-text-primary)]">Admin Dashboard</h2>
            <p className="text-gray-600 text-lg">Manage your properties, rooms, and system settings</p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 flex-wrap">
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-8 py-4 font-bold text-base transition-all ${
                activeTab === 'settings'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-white text-[var(--color-text-primary)] hover:bg-[var(--color-border)]'
              }`}
            >
              SETTINGS & API KEYS
            </button>
            <button
              onClick={() => setActiveTab('media')}
              className={`px-8 py-4 font-bold text-base transition-all ${
                activeTab === 'media'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-white text-[var(--color-text-primary)] hover:bg-[var(--color-border)]'
              }`}
            >
              ROOM MEDIA
            </button>
            <button
              onClick={() => setActiveTab('theme')}
              className={`px-8 py-4 font-bold text-base transition-all ${
                activeTab === 'theme'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-white text-[var(--color-text-primary)] hover:bg-[var(--color-border)]'
              }`}
            >
              🎨 THEME & BRANDING
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`px-8 py-4 font-bold text-base transition-all ${
                activeTab === 'email'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-white text-[var(--color-text-primary)] hover:bg-[var(--color-border)]'
              }`}
            >
              📧 EMAIL
            </button>
            <button
              onClick={() => setActiveTab('promo')}
              className={`px-8 py-4 font-bold text-base transition-all ${
                activeTab === 'promo'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-white text-[var(--color-text-primary)] hover:bg-[var(--color-border)]'
              }`}
            >
              🏷️ PROMO KODOVI
            </button>
            <button
              onClick={() => setActiveTab('cards')}
              className={`px-8 py-4 font-bold text-base transition-all ${
                activeTab === 'cards'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-white text-[var(--color-text-primary)] hover:bg-[var(--color-border)]'
              }`}
            >
              📋 INFO KARTICE
            </button>
            <button
              onClick={() => setActiveTab('rates')}
              className={`px-8 py-4 font-bold text-base transition-all ${
                activeTab === 'rates'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-white text-[var(--color-text-primary)] hover:bg-[var(--color-border)]'
              }`}
            >
              📊 RATE-OVI
            </button>
          </div>

          {/* Settings Tab Content */}
          {activeTab === 'settings' && (
            <>
          {/* API Configuration */}
          <Card className="mb-6" data-testid="admin-settings-form">
            <CardHeader>
              <CardTitle>API Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <PmsSettingsCard settings={settings} setSettings={setSettings} onAutofill={(prop) => {
                setNewProperty({ property_id: prop.id, property_name: prop.name, room_ids: (prop.rooms || []).map(r => r.id).join(', ') });
                toast.success('Obrazac popunjen - provjerite i kliknite Add Property');
              }} />

              <div className="border-t border-stone-200 pt-6">
                <h3 className="text-lg font-medium mb-4">Stripe Configuration</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="stripeSecret">Stripe Secret Key</Label>
                    <Input
                      id="stripeSecret"
                      type="password"
                      value={settings.stripe_api_key}
                      onChange={(e) => setSettings({ ...settings, stripe_api_key: e.target.value })}
                      placeholder="sk_test_..."
                      className="font-mono text-sm"
                      data-testid="stripe-secret-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="stripePublishable">Stripe Publishable Key</Label>
                    <Input
                      id="stripePublishable"
                      type="text"
                      value={settings.stripe_publishable_key}
                      onChange={(e) => setSettings({ ...settings, stripe_publishable_key: e.target.value })}
                      placeholder="pk_test_..."
                      className="font-mono text-sm"
                      data-testid="stripe-publishable-input"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Button 
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="w-full md:w-auto"
                  data-testid="save-settings-button"
                >
                  {saving ? 'Saving...' : 'Save API Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Property Management */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Property & Room Configuration</CardTitle>
              <p className="text-sm text-stone-600 mt-1">Dodajte PMS property ID i ID-jeve jedinica (soba)</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Existing Properties */}
              {settings.property_configs && settings.property_configs.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium">Configured Properties</h4>
                  {settings.property_configs.map((prop) => (
                    <div key={prop.property_id} className="border border-stone-200 rounded p-4">
                      {editingProperty?.property_id === prop.property_id ? (
                        // Edit mode
                        <div className="space-y-3">
                          <div>
                            <Label>Property Name</Label>
                            <Input
                              value={editingProperty.property_name}
                              onChange={(e) => setEditingProperty({...editingProperty, property_name: e.target.value})}
                            />
                            <p className="text-xs text-stone-500 mt-1">Ime za identifikaciju u admin panelu</p>
                          </div>
                          <div className="bg-amber-50 border border-amber-200 rounded p-3">
                            <Label className="text-amber-800 font-semibold flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                              </svg>
                              Ime grupe (Group Name)
                            </Label>
                            <Input
                              value={editingProperty.group_name || ''}
                              onChange={(e) => setEditingProperty({...editingProperty, group_name: e.target.value})}
                              placeholder="npr. Apartments Imperial"
                              className="mt-2"
                            />
                            <p className="text-xs text-amber-700 mt-2">
                              <strong>Važno:</strong> Sve nekretnine s istim "imenom grupe" bit će prikazane zajedno pod tim imenom na stranici za goste.
                              <br />Ostavite prazno ako ne želite grupirati ovu nekretninu s drugima.
                            </p>
                          </div>
                          <div>
                            <Label>Property ID</Label>
                            <Input
                              value={editingProperty.property_id}
                              disabled
                              className="bg-stone-100"
                            />
                          </div>
                          <div>
                            <Label>Room IDs (comma-separated)</Label>
                            <Input
                              value={editingProperty.room_ids}
                              onChange={(e) => setEditingProperty({...editingProperty, room_ids: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Prioritet prikaza (Display Order)</Label>
                            <Input
                              type="number"
                              value={editingProperty.display_order || 0}
                              onChange={(e) => setEditingProperty({...editingProperty, display_order: parseInt(e.target.value) || 0})}
                              placeholder="0"
                            />
                            <p className="text-xs text-stone-500 mt-1">Manji broj = viši prioritet (prikazuje se prvi). 0 = zadani.</p>
                          </div>
                          {/* Location Settings */}
                          <div className="border-t pt-3 mt-3">
                            <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              Location (for Map)
                            </h5>
                            <div className="space-y-2">
                              <div>
                                <Label className="text-xs">Address</Label>
                                <Input
                                  value={editingProperty.address || ''}
                                  onChange={(e) => setEditingProperty({...editingProperty, address: e.target.value})}
                                  placeholder="e.g., Korzo 12, 51000 Rijeka, Croatia"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs">Latitude</Label>
                                  <Input
                                    type="number"
                                    step="any"
                                    value={editingProperty.latitude || ''}
                                    onChange={(e) => setEditingProperty({...editingProperty, latitude: e.target.value ? parseFloat(e.target.value) : null})}
                                    placeholder="45.3271"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Longitude</Label>
                                  <Input
                                    type="number"
                                    step="any"
                                    value={editingProperty.longitude || ''}
                                    onChange={(e) => setEditingProperty({...editingProperty, longitude: e.target.value ? parseFloat(e.target.value) : null})}
                                    placeholder="14.4422"
                                  />
                                </div>
                              </div>
                              <p className="text-xs text-stone-500">Tip: Find coordinates on Google Maps by right-clicking on a location</p>
                            </div>
                          </div>
                          
                          {/* Pricing Policy Section */}
                          <div className="border-t border-stone-200 pt-4 mt-4">
                            <Label className="text-sm font-semibold mb-3 block">Pravilnik o cijenama (Pricing Policy)</Label>
                            <p className="text-xs text-stone-500 mb-3">Tekst koji se prikazuje gostima. Možete dodati pravila o dodatnim gostima, check-in/out vremena, uvjete otkaza itd.</p>
                            <div className="space-y-3">
                              <div>
                                <Label className="text-xs">Hrvatski (HR)</Label>
                                <textarea
                                  className="w-full p-2 border border-stone-300 rounded text-sm min-h-[100px]"
                                  value={editingProperty.pricing_policy_hr || ''}
                                  onChange={(e) => setEditingProperty({...editingProperty, pricing_policy_hr: e.target.value})}
                                  placeholder="• Prijava u objekt moguća je nakon 15:00&#10;• Odjava iz objekta do 10:00&#10;• Dodatni gost: €15 po noći"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">English (EN)</Label>
                                <textarea
                                  className="w-full p-2 border border-stone-300 rounded text-sm min-h-[100px]"
                                  value={editingProperty.pricing_policy_en || ''}
                                  onChange={(e) => setEditingProperty({...editingProperty, pricing_policy_en: e.target.value})}
                                  placeholder="• Check-in after 3:00 PM&#10;• Check-out by 10:00 AM&#10;• Extra guest: €15 per night"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Deutsch (DE)</Label>
                                <textarea
                                  className="w-full p-2 border border-stone-300 rounded text-sm min-h-[100px]"
                                  value={editingProperty.pricing_policy_de || ''}
                                  onChange={(e) => setEditingProperty({...editingProperty, pricing_policy_de: e.target.value})}
                                  placeholder="• Check-in ab 15:00 Uhr&#10;• Check-out bis 10:00 Uhr&#10;• Zusätzlicher Gast: €15 pro Nacht"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Italiano (IT)</Label>
                                <textarea
                                  className="w-full p-2 border border-stone-300 rounded text-sm min-h-[100px]"
                                  value={editingProperty.pricing_policy_it || ''}
                                  onChange={(e) => setEditingProperty({...editingProperty, pricing_policy_it: e.target.value})}
                                  placeholder="• Check-in dopo le 15:00&#10;• Check-out entro le 10:00&#10;• Ospite extra: €15 a notte"
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button onClick={handleSaveEdit} size="sm">Save</Button>
                            <Button onClick={() => setEditingProperty(null)} variant="outline" size="sm">Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h5 className="font-medium">{prop.property_name}</h5>
                            {prop.group_name && (
                              <p className="text-sm text-amber-700 font-medium flex items-center gap-1 mt-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                                Grupa: {prop.group_name}
                              </p>
                            )}
                            <p className="text-sm text-stone-600">Property ID: {prop.property_id}</p>
                            <p className="text-sm text-stone-600">Room IDs: {prop.room_ids.join(', ')}</p>
                            <p className="text-sm text-stone-600">Prioritet: {prop.display_order || 0}</p>
                            {prop.address && (
                              <p className="text-sm text-stone-600 flex items-center gap-1 mt-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                </svg>
                                {prop.address}
                              </p>
                            )}
                            {prop.latitude && prop.longitude && (
                              <p className="text-xs text-stone-500">📍 {prop.latitude.toFixed(5)}, {prop.longitude.toFixed(5)}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditProperty(prop)}
                              data-testid={`edit-property-${prop.property_id}`}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteProperty(prop.property_id);
                              }}
                              className="bg-red-600 hover:bg-red-700 text-white"
                              data-testid={`delete-property-${prop.property_id}`}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Property */}
              <div className="border-t border-stone-200 pt-6">
                <h4 className="font-medium mb-4">Add New Property</h4>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="propertyId">Property ID</Label>
                    <Input
                      id="propertyId"
                      type="text"
                      value={newProperty.property_id}
                      onChange={(e) => setNewProperty({ ...newProperty, property_id: e.target.value })}
                      placeholder="27387644-6437-..."
                      data-testid="property-id-input"
                    />
                    <p className="text-xs text-stone-500 mt-1">PMS Property ID (UUID) — vidi "Testiraj PMS vezu" iznad</p>
                  </div>

                  <div>
                    <Label htmlFor="propertyName">Property Name</Label>
                    <Input
                      id="propertyName"
                      type="text"
                      value={newProperty.property_name}
                      onChange={(e) => setNewProperty({ ...newProperty, property_name: e.target.value })}
                      placeholder="Luxury Downtown Apartment"
                      data-testid="property-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="roomIds">Room IDs (comma-separated)</Label>
                    <Input
                      id="roomIds"
                      type="text"
                      value={newProperty.room_ids}
                      onChange={(e) => setNewProperty({ ...newProperty, room_ids: e.target.value })}
                      placeholder="uuid-1, uuid-2"
                      data-testid="room-ids-input"
                    />
                    <p className="text-xs text-stone-500 mt-1">Enter room IDs separated by commas</p>
                  </div>

                  <Button 
                    onClick={handleAddProperty}
                    data-testid="add-property-button"
                  >
                    Add Property
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Property Owners Manager - GDPR Data Controllers */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Property Owners (GDPR Data Controllers)</CardTitle>
              <p className="text-sm text-stone-600 mt-1">
                Manage property owners for Privacy Policy and Terms & Conditions compliance
              </p>
            </CardHeader>
            <CardContent>
              <PropertyOwnersManager />
            </CardContent>
          </Card>
          </>
          )}

          {/* Room Media Tab Content */}
          {activeTab === 'media' && (
            <RoomMediaManager />
          )}

          {/* Theme & Branding Tab Content */}
          {activeTab === 'theme' && (
            <ThemeSettings />
          )}

          {/* Email Settings Tab Content */}
          {activeTab === 'email' && (
            <EmailSettingsManager />
          )}

          {activeTab === 'promo' && (
            <PromoCodesManager />
          )}

          {activeTab === 'cards' && (
            <InfoCardsManager />
          )}

          {activeTab === 'rates' && (
            <RatesManager token={localStorage.getItem('admin_token')} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;